'use client';

import React, { useState } from 'react';
import { X, GitBranch, MapPin } from 'lucide-react';
import { Branch, BranchStatus, MOCK_FARMS } from './farmsData';

interface CreateBranchModalProps {
  farmId: string;
  onClose: () => void;
  onSave: (data: Partial<Branch>) => void;
}

export default function CreateBranchModal({ farmId, onClose, onSave }: CreateBranchModalProps) {
  const farm = MOCK_FARMS.find(f => f.id === farmId);
  const [form, setForm] = useState({
    branchName: '',
    branchCode: '',
    manager: '',
    managerPhone: '',
    country: 'Uganda',
    district: farm?.district || '',
    subcounty: '',
    village: '',
    gpsLat: '',
    gpsLng: '',
    phone: '',
    email: '',
    openingDate: '',
    capacity: '',
    status: 'active' as BranchStatus,
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  function validate() {
    const e: Record<string, string> = {};
    if (!form.branchName.trim()) e.branchName = 'Branch name is required';
    if (!form.branchCode.trim()) e.branchCode = 'Branch code is required';
    if (!form.manager.trim()) e.manager = 'Manager is required';
    if (!form.capacity || isNaN(Number(form.capacity))) e.capacity = 'Valid capacity required';
    return e;
  }

  function handleChange(field: string, value: string) {
    setForm(f => ({ ...f, [field]: value }));
    if (errors[field]) setErrors(e => { const n = { ...e }; delete n[field]; return n; });
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setSaving(true);
    setTimeout(() => {
      onSave({ ...form, farmId, capacity: Number(form.capacity), currentBirds: 0, activeHouses: 0, activeFlocks: 0 });
      setSaving(false);
    }, 600);
  }

  const Field = ({ label, field, type = 'text', required = false, placeholder = '', span2 = false }: { label: string; field: string; type?: string; required?: boolean; placeholder?: string; span2?: boolean }) => (
    <div className={span2 ? 'md:col-span-2' : ''}>
      <label className="block text-xs font-semibold text-gray-600 mb-1">{label}{required && <span className="text-red-500 ml-0.5">*</span>}</label>
      <input
        type={type}
        value={(form as Record<string, string>)[field]}
        onChange={e => handleChange(field, e.target.value)}
        placeholder={placeholder}
        className={`input-field ${errors[field] ? 'border-red-400' : ''}`}
      />
      {errors[field] && <p className="text-xs text-red-500 mt-0.5">{errors[field]}</p>}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
              <GitBranch size={16} className="text-blue-700" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">Add Branch</h2>
              {farm && <p className="text-xs text-gray-400">Under: {farm.farmName}</p>}
            </div>
          </div>
          <button onClick={onClose} className="btn-ghost p-1.5"><X size={16} /></button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto scrollbar-thin p-6 space-y-5">
          <div>
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Branch Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Branch Name" field="branchName" required placeholder="e.g. Idudi Main Site" />
              <Field label="Branch Code" field="branchCode" required placeholder="e.g. IDF-B01" />
              <Field label="Manager Name" field="manager" required placeholder="Full name" />
              <Field label="Manager Phone" field="managerPhone" placeholder="+256 772 000 000" />
              <Field label="Capacity (birds)" field="capacity" type="number" required placeholder="e.g. 5000" />
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Status</label>
                <select className="select-field" value={form.status} onChange={e => handleChange('status', e.target.value)}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <Field label="Opening Date" field="openingDate" type="date" />
              <Field label="Phone" field="phone" placeholder="+256 414 000 000" />
              <Field label="Email" field="email" type="email" placeholder="branch@example.com" span2 />
            </div>
          </div>

          <div>
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
              <MapPin size={12} />Location
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Field label="Country" field="country" placeholder="Uganda" />
              <Field label="District" field="district" placeholder="e.g. Iganga" />
              <Field label="Subcounty" field="subcounty" placeholder="e.g. Idudi" />
              <Field label="Village" field="village" placeholder="e.g. Nabitende" />
              <Field label="GPS Latitude" field="gpsLat" placeholder="0.4521" />
              <Field label="GPS Longitude" field="gpsLng" placeholder="33.4812" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Notes</label>
            <textarea
              value={form.notes}
              onChange={e => handleChange('notes', e.target.value)}
              rows={2}
              placeholder="Optional notes about this branch..."
              className="input-field resize-none"
            />
          </div>
        </form>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={handleSubmit} disabled={saving} className="btn-primary">
            {saving ? 'Saving…' : 'Save Branch'}
          </button>
        </div>
      </div>
    </div>
  );
}
